import type {
  RoutingLoadEffectiveSettings,
  RoutingLoadScopeKind,
  RoutingLoadScopeSettingsValues,
  UpdateRoutingLoadScopeSettingsRequest,
} from '@nadoutong/contracts';
import { useState } from 'react';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import { Chip, Input, RadioGroup } from '../../ui/index.ts';
import type { ChipTone, RadioOption } from '../../ui/index.ts';
import {
  SCOPE_BOOLEAN_FIELDS,
  SCOPE_NUMERIC_FIELDS,
  booleanTriState,
  booleanTriStatePatch,
  killSwitchForBooleanField,
  numericOverlayPatch,
  resolveScopeBoolean,
  resolveScopeTimeout,
  timeoutTriState,
  timeoutTriStatePatch,
} from './loadScopeFields.ts';
import type {
  ScopeBooleanField,
  ScopeFieldSource,
  ScopeNumericField,
  ScopeOverlayField,
} from './loadScopeFields.ts';
import { numericFieldMeta, parseNumericInput } from './loadSettingsFields.ts';

const FIELD_LABEL: Record<ScopeOverlayField, I18nKey> = {
  l2_enabled: 'tenant_routing.load.settings.field.l2_enabled',
  l3_enabled: 'tenant_routing.load.settings.field.l3_enabled',
  l4_enabled: 'tenant_routing.load.settings.field.l4_enabled',
  l3_inferred_caps_enabled: 'tenant_routing.load.settings.field.l3_inferred_caps_enabled',
  l2_observed_caps_enabled: 'tenant_routing.load.settings.field.l2_observed_caps_enabled',
  affinity_conversation_enabled: 'tenant_routing.load.settings.field.affinity_conversation_enabled',
  attempt_first_byte_timeout_ms: 'tenant_routing.load.settings.field.attempt_first_byte_timeout_ms',
  session_rehome_enabled: 'tenant_routing.load.settings.field.session_rehome_enabled',
  shed_warm_threshold: 'tenant_routing.load.settings.field.shed_warm_threshold',
  session_rehome_after_429: 'tenant_routing.load.settings.field.session_rehome_after_429',
  session_rehome_ttl_seconds: 'tenant_routing.load.settings.field.session_rehome_ttl_seconds',
  session_bucket_rpm: 'tenant_routing.load.settings.field.session_bucket_rpm',
  sticky_escape_error_rate: 'tenant_routing.load.settings.field.sticky_escape_error_rate',
  pool_key_rpm_ceiling: 'tenant_routing.load.settings.field.pool_key_rpm_ceiling',
};

const FIELD_DESC: Record<ScopeNumericField, I18nKey> = {
  shed_warm_threshold: 'tenant_routing.load.settings.field.shed_warm_threshold_desc',
  session_rehome_after_429: 'tenant_routing.load.settings.field.session_rehome_after_429_desc',
  session_rehome_ttl_seconds: 'tenant_routing.load.settings.field.session_rehome_ttl_seconds_desc',
  session_bucket_rpm: 'tenant_routing.load.settings.field.session_bucket_rpm_desc',
  sticky_escape_error_rate: 'tenant_routing.load.settings.field.sticky_escape_error_rate_desc',
  pool_key_rpm_ceiling: 'tenant_routing.load.settings.field.pool_key_rpm_ceiling_desc',
};

const SOURCE_TONE: Record<ScopeFieldSource, ChipTone> = {
  sku: 'signal',
  namespace: 'ok',
  global: 'neutral',
};

const SOURCE_LABEL: Record<ScopeFieldSource, I18nKey> = {
  sku: 'tenant_routing.load.scope.source.sku',
  namespace: 'tenant_routing.load.scope.source.namespace',
  global: 'tenant_routing.load.scope.source.global',
};

export interface ScopeKillSwitches {
  readonly l2: boolean;
  readonly l3: boolean;
  readonly l4: boolean;
  readonly sessionRehome: boolean;
}

function isKilled(field: ScopeBooleanField, kill: ScopeKillSwitches): boolean {
  const key = killSwitchForBooleanField(field);
  if (key === 'l2_env_kill_switch') return kill.l2;
  if (key === 'l3_env_kill_switch') return kill.l3;
  if (key === 'l4_env_kill_switch') return kill.l4;
  if (key === 'session_rehome_env_kill_switch') return kill.sessionRehome;
  return false;
}

export function RoutingLoadScopeEditor({
  kind,
  scopeId,
  skuValues,
  namespaceValues,
  globalEffective,
  kill,
  saving,
  onPatch,
}: {
  readonly kind: RoutingLoadScopeKind;
  readonly scopeId: string;
  readonly skuValues: RoutingLoadScopeSettingsValues | null;
  readonly namespaceValues: RoutingLoadScopeSettingsValues | null;
  readonly globalEffective: RoutingLoadEffectiveSettings;
  readonly kill: ScopeKillSwitches;
  readonly saving: boolean;
  readonly onPatch: (body: UpdateRoutingLoadScopeSettingsRequest) => void;
}): JSX.Element {
  const t = useT();
  const stored = kind === 'sku' ? skuValues : namespaceValues;
  const prefix = `routing-load-scope-${kind}-${scopeId}`;

  const triOptions: readonly RadioOption[] = [
    { value: 'follow', label: t('tenant_routing.load.scope.tri.follow') },
    { value: 'on', label: t('tenant_routing.load.scope.tri.on') },
    { value: 'off', label: t('tenant_routing.load.scope.tri.off') },
  ];
  const timeoutOptions: readonly RadioOption[] = [
    { value: 'follow', label: t('tenant_routing.load.scope.tri.follow') },
    { value: 'on', label: t('tenant_routing.load.scope.tri.on') },
  ];

  return (
    <div className="flex flex-col gap-2" data-testid={`${prefix}-fields`}>
      {SCOPE_BOOLEAN_FIELDS.map((field) => {
        const current = booleanTriState(stored?.[field]);
        const resolved = resolveScopeBoolean({
          sku: skuValues?.[field],
          namespace: namespaceValues?.[field],
          globalEffective: globalEffective[field],
          envKilled: isKilled(field, kill),
        });
        const disabled = saving || isKilled(field, kill);
        return (
          <div
            key={field}
            className="flex flex-col gap-1.5 rounded border border-line-soft bg-card px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
            data-testid={`${prefix}-${field}`}
          >
            <p className="m-0 text-label text-paper">{t(FIELD_LABEL[field])}</p>
            <div className="flex flex-wrap items-center gap-3">
              <RadioGroup
                value={current}
                onValueChange={(next) => {
                  if (next !== 'follow' && next !== 'on' && next !== 'off') return;
                  if (next === current) return;
                  onPatch(booleanTriStatePatch(field, next));
                }}
                options={triOptions.map((option) => ({ ...option, disabled }))}
                orientation="horizontal"
                ariaLabel={t(FIELD_LABEL[field])}
                testId={`${prefix}-${field}-tri`}
              />
              <EffectiveChip
                on={resolved.value}
                source={resolved.source}
                testId={`${prefix}-${field}-effective`}
              />
            </div>
          </div>
        );
      })}
      {(() => {
        const current = timeoutTriState(stored?.attempt_first_byte_timeout_ms);
        const resolved = resolveScopeTimeout({
          sku: skuValues?.attempt_first_byte_timeout_ms,
          namespace: namespaceValues?.attempt_first_byte_timeout_ms,
          globalEffective: globalEffective.attempt_first_byte_timeout_ms,
        });
        return (
          <div
            className="flex flex-col gap-1.5 rounded border border-line-soft bg-card px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
            data-testid={`${prefix}-attempt_first_byte_timeout_ms`}
          >
            <p className="m-0 text-label text-paper">
              {t(FIELD_LABEL.attempt_first_byte_timeout_ms)}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <RadioGroup
                value={current}
                onValueChange={(next) => {
                  if (next !== 'follow' && next !== 'on') return;
                  if (next === current) return;
                  onPatch(timeoutTriStatePatch(next));
                }}
                options={timeoutOptions.map((option) => ({ ...option, disabled: saving }))}
                orientation="horizontal"
                ariaLabel={t(FIELD_LABEL.attempt_first_byte_timeout_ms)}
                testId={`${prefix}-attempt_first_byte_timeout_ms-tri`}
              />
              <EffectiveChip
                on={resolved.value !== null}
                source={resolved.source}
                testId={`${prefix}-attempt_first_byte_timeout_ms-effective`}
              />
            </div>
          </div>
        );
      })()}
      {SCOPE_NUMERIC_FIELDS.map((field) => (
        <NumericOverlayRow
          key={field}
          field={field}
          prefix={prefix}
          stored={stored?.[field] ?? null}
          skuValue={skuValues?.[field]}
          namespaceValue={namespaceValues?.[field]}
          globalEffective={globalEffective[field]}
          saving={saving}
          onPatch={onPatch}
        />
      ))}
    </div>
  );
}

function NumericOverlayRow({
  field,
  prefix,
  stored,
  skuValue,
  namespaceValue,
  globalEffective,
  saving,
  onPatch,
}: {
  readonly field: ScopeNumericField;
  readonly prefix: string;
  readonly stored: number | null;
  readonly skuValue: number | null | undefined;
  readonly namespaceValue: number | null | undefined;
  readonly globalEffective: number | null;
  readonly saving: boolean;
  readonly onPatch: (body: UpdateRoutingLoadScopeSettingsRequest) => void;
}): JSX.Element {
  const t = useT();
  const [draft, setDraft] = useState<string | undefined>(undefined);
  const [error, setError] = useState(false);
  const resolved = resolveScopeTimeout({
    sku: skuValue,
    namespace: namespaceValue,
    globalEffective,
  });
  const meta = numericFieldMeta(field);
  const inputValue = draft ?? (stored === null ? '' : String(stored));
  const fieldId = `${prefix}-${field}`;
  const commit = (raw: string): void => {
    const parsed = parseNumericInput(field, raw, stored);
    if (parsed.kind === 'invalid') {
      setError(true);
      return;
    }
    setError(false);
    if (parsed.kind === 'unchanged') {
      setDraft(undefined);
      return;
    }
    setDraft(undefined);
    if (parsed.kind === 'clear') onPatch(numericOverlayPatch(field, null));
    if (parsed.kind === 'value') onPatch(numericOverlayPatch(field, parsed.value));
  };
  return (
    <div
      className="flex flex-col gap-1.5 rounded border border-line-soft bg-card px-3 py-2"
      data-testid={fieldId}
    >
      <label htmlFor={`${fieldId}-input`} className="m-0 text-label text-paper">
        {t(FIELD_LABEL[field])}
      </label>
      <p className="m-0 text-dense leading-5 text-paper-low">
        {t(FIELD_DESC[field])}
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <Input
          id={`${fieldId}-input`}
          mono
          inputMode={meta.kind === 'decimal' ? 'decimal' : 'numeric'}
          className="max-w-[9rem]"
          placeholder={resolved.value === null ? '' : String(resolved.value)}
          value={inputValue}
          invalid={error}
          disabled={saving}
          onChange={(event) => {
            setDraft(event.target.value);
            setError(false);
          }}
          onBlur={(event) => commit(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') commit(event.currentTarget.value);
          }}
          data-testid={`${fieldId}-input`}
        />
        {resolved.value !== null ? (
          <span className="text-dense text-paper-low" data-testid={`${fieldId}-effective`}>
            {t('tenant_routing.load.settings.effective', { value: String(resolved.value) })}
          </span>
        ) : null}
        <Chip tone={SOURCE_TONE[resolved.source]} lamp={false} testId={`${fieldId}-effective-source`}>
          {t(SOURCE_LABEL[resolved.source])}
        </Chip>
      </div>
      {error ? (
        <p role="alert" className="m-0 text-label text-bad">
          {t(meta.kind === 'int'
            ? 'tenant_routing.load.settings.invalid_int'
            : 'tenant_routing.load.settings.invalid_decimal')}
        </p>
      ) : null}
    </div>
  );
}

function EffectiveChip({
  on,
  source,
  testId,
}: {
  readonly on: boolean;
  readonly source: ScopeFieldSource;
  readonly testId: string;
}): JSX.Element {
  const t = useT();
  return (
    <span className="inline-flex items-center gap-1.5" data-testid={testId}>
      <span className="text-micro text-paper-low">{t('tenant_routing.load.scope.effective')}</span>
      <Chip tone={on ? 'ok' : 'neutral'} lamp={false}>
        {on ? t('tenant_routing.load.scope.tri.on') : t('tenant_routing.load.scope.tri.off')}
      </Chip>
      <Chip tone={SOURCE_TONE[source]} lamp={false} testId={`${testId}-source`}>
        {t(SOURCE_LABEL[source])}
      </Chip>
    </span>
  );
}


